// The typed headcount boxes beside Available MP / day on the Crew Schedule:
// what each one is. The box itself, and everything they share, is
// TypedHeadcountBox.tsx. Each mirrors a figure in the API (temps-active.ts,
// subcontractors-active.ts), where its decision record lives.

import { manpowerApi } from '@/lib/apiClient';
import { SUBCONTRACTORS_ACTIVE_QUERY_KEY, TEMPS_ACTIVE_QUERY_KEY } from '@/lib/queryClient';

export interface TypedHeadcount {
  /** Null until somebody enters it. Never 0 by default, because zero is a claim. */
  count: number | null;
  /** When it was last saved, as an ISO instant. Null until it ever is. */
  updatedAt: string | null;
}

export interface TypedHeadcountFigure {
  /** The mp_config key. Mirrors the API figure's key. */
  key: string;
  /** The box's label, its input's accessible name, and its Admin → Config row. */
  label: string;
  queryKey: readonly unknown[];
  fetch: () => Promise<TypedHeadcount>;
  /**
   * ⚠️ Whether the figure is counted INSIDE Available MP / day. It decides the
   * caption ("Included in" or "Not included in Available MP") and whether a
   * count above Available MP is flagged. Mirrors the API figure's
   * `inAvailableMp`, which decides what setConfig refuses;
   * typedHeadcounts.spec.ts fails if the two differ.
   */
  inAvailableMp: boolean;
  /** Hover text for the whole box. */
  title: string;
  /** Hover text on the number, for editors. */
  editHint: string;
}

/** The mp_config key. Mirrors TEMPS_ACTIVE_KEY in the API's temps-active.ts. */
export const TEMPS_ACTIVE_KEY = 'mpTempsActive';

/**
 * Temps active (#584). ⚠️ Temps are counted INSIDE Available MP, not on top of
 * it, and the caption says so on purpose: read the other way, the two boxes side
 * by side overstate capacity by exactly the temp headcount.
 */
export const TEMPS_ACTIVE: TypedHeadcountFigure = {
  key: TEMPS_ACTIVE_KEY,
  label: 'Temps active',
  queryKey: TEMPS_ACTIVE_QUERY_KEY,
  fetch: manpowerApi.tempsActive,
  inAvailableMp: true,
  title:
    'Temps working now, entered by hand by the manpower scheduler. They are already counted '
    + 'in Available MP / day, not added on top of it. Not tied to the crew on the schedule.',
  editHint: 'Click to enter how many temps are working now',
};

/** The mp_config key. Mirrors SUBCONTRACTORS_ACTIVE_KEY in the API's subcontractors-active.ts. */
export const SUBCONTRACTORS_ACTIVE_KEY = 'mpSubcontractorsActive';

/**
 * Subcontractors (#688): subcontractor workers on local jobsites now, owned and
 * entered by the Training and Crew Scheduling Manager. ⚠️ Counted INSIDE
 * Available MP, like temps (decided on #688, 2026-10-22), so the two share its
 * ceiling: each box flags, and the API refuses, temps + subcontractors above it.
 * Local jobsites only, but the label stays "Subcontractors" (also #688).
 */
export const SUBCONTRACTORS_ACTIVE: TypedHeadcountFigure = {
  key: SUBCONTRACTORS_ACTIVE_KEY,
  label: 'Subcontractors',
  queryKey: SUBCONTRACTORS_ACTIVE_QUERY_KEY,
  fetch: manpowerApi.subcontractorsActive,
  inAvailableMp: true,
  title:
    'Subcontractor workers on our local jobsites now, filling gaps for specific tasks, entered by '
    + 'hand by the Training and Crew Scheduling Manager. They are already counted in Available '
    + 'MP / day, not added on top of it. Not tied to the crew on the schedule.',
  editHint: 'Click to enter how many subcontractor workers are on our local jobsites now',
};

/** Every typed box, in the order they sit after Available MP / day. */
export const TYPED_HEADCOUNTS: readonly TypedHeadcountFigure[] = [TEMPS_ACTIVE, SUBCONTRACTORS_ACTIVE];

/** What the caption says about Available MP. */
export function availableMpRelation(figure: TypedHeadcountFigure): string {
  return figure.inAvailableMp ? 'Included in Available MP' : 'Not included in Available MP';
}

/** The figure's row label on Admin → Config, where it isn't edited. */
export function adminConfigLabel(figure: TypedHeadcountFigure): string {
  const relation = availableMpRelation(figure);
  return `${figure.label} (Crew Schedule; ${relation[0].toLowerCase()}${relation.slice(1)})`;
}
